import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { Structure, User, ProcedureRequest } from '../types';
import RichTextEditor from '../components/RichTextEditor';
import {
  ChevronRightIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  BuildingOffice2Icon,
  UserCircleIcon,
  ExclamationCircleIcon,
  ArrowLeftIcon,
  ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/outline';

type FormErrors = Partial<Record<keyof ProcedureRequest, string>>;

interface SectionProps {
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  children: React.ReactNode;
}

const FormSection: React.FC<SectionProps> = ({ icon: Icon, title, children }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2">
      <div className="w-7 h-7 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
        <Icon className="h-4 w-4 text-primary-600" />
      </div>
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{title}</h3>
    </div>
    <div className="pl-9 space-y-4">{children}</div>
  </div>
);

interface FieldProps {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  hint?: string;
}

const Field: React.FC<FieldProps> = ({ label, required, error, children, hint }) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-gray-700">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {hint && !error && <p className="text-xs text-gray-400">{hint}</p>}
    {error && (
      <p className="flex items-center gap-1 text-xs text-red-600 font-medium mt-1">
        <ExclamationCircleIcon className="h-3.5 w-3.5 flex-shrink-0" />
        {error}
      </p>
    )}
  </div>
);

const inputCls = (error?: string) =>
  `block w-full px-3.5 py-2.5 rounded-xl border text-sm placeholder-gray-400 transition-colors
   focus:outline-none focus:ring-2 focus:ring-offset-0
   ${error
     ? 'border-red-400 bg-red-50 focus:ring-red-400 focus:border-red-400'
     : 'border-gray-200 bg-white focus:ring-primary-500 focus:border-primary-500'
   }`;

const selectCls = (error?: string, disabled?: boolean) =>
  `block w-full px-3.5 py-2.5 rounded-xl border text-sm transition-colors
   focus:outline-none focus:ring-2 focus:ring-offset-0
   ${disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed border-gray-200' : ''}
   ${!disabled && error
     ? 'border-red-400 bg-red-50 focus:ring-red-400 focus:border-red-400'
     : !disabled
     ? 'border-gray-200 bg-white focus:ring-primary-500 focus:border-primary-500'
     : ''}`;

const FormSkeleton: React.FC = () => (
  <div className="w-full animate-pulse space-y-6">
    <div className="h-5 bg-gray-200 rounded w-48" />
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="bg-primary-600 px-8 py-6">
        <div className="h-7 bg-primary-500 rounded w-48 mb-2" />
        <div className="h-4 bg-primary-500 rounded w-64 opacity-60" />
      </div>
      <div className="px-8 py-6 space-y-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-gray-200 rounded-lg" />
              <div className="h-4 bg-gray-200 rounded w-32" />
            </div>
            <div className="pl-9 space-y-4">
              <div className="h-10 bg-gray-100 rounded-xl" />
              <div className="h-10 bg-gray-100 rounded-xl" />
            </div>
          </div>
        ))}
      </div>
      <div className="px-8 py-5 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
        <div className="h-10 w-24 bg-gray-200 rounded-xl" />
        <div className="h-10 w-28 bg-primary-200 rounded-xl" />
      </div>
    </div>
  </div>
);

const ProcedureForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const isEditing = !!id;

  const [structures, setStructures] = useState<Structure[]>([]);
  const [responsables, setResponsables] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState<ProcedureRequest>({
    nom: '',
    description: '',
    annee: new Date().getFullYear(),
    dateDebut: '',
    dateFin: '',
    structureId: '',
    structureBeneficiaireId: '',
    observations: '',
    responsableId: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const structuresData = await apiService.getStructures();
        setStructures(structuresData);

        if (!isSuperAdmin() && user?.structureId) {
          setForm((prev) => ({ ...prev, structureId: user.structureId! }));
          const responsablesData = await apiService.getResponsablesForStructure(user.structureId);
          setResponsables(responsablesData);
        }

        if (isEditing && id) {
          const procedure = await apiService.getProcedure(id);
          setForm({
            nom: procedure.nom,
            description: procedure.description || '',
            annee: procedure.annee,
            dateDebut: procedure.dateDebut,
            dateFin: procedure.dateFin,
            structureId: procedure.structureId,
            structureBeneficiaireId: procedure.structureBeneficiaireId || '',
            observations: procedure.observations || '',
            responsableId: procedure.responsableId || '',
          });
          if (procedure.structureId) {
            const responsablesData = await apiService.getResponsablesForStructure(procedure.structureId);
            setResponsables(responsablesData);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement:', error);
        setApiError('Erreur lors du chargement des données');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id, isEditing, isSuperAdmin, user?.structureId]);

  const handleStructureChange = async (structureId: string) => {
    setForm((prev) => ({ ...prev, structureId, responsableId: '' }));
    if (structureId) {
      try {
        const responsablesData = await apiService.getResponsablesForStructure(structureId);
        setResponsables(responsablesData);
      } catch (error) {
        console.error('Erreur lors du chargement des responsables:', error);
      }
    } else {
      setResponsables([]);
    }
  };

  const validate = (): FormErrors => {
    const errs: FormErrors = {};
    if (!form.nom.trim()) errs.nom = 'Le nom est obligatoire.';
    if (!form.structureId) errs.structureId = 'Veuillez sélectionner une structure de développement.';
    if (!form.dateDebut) errs.dateDebut = 'La date de début est obligatoire.';
    if (!form.dateFin) errs.dateFin = 'La date de fin est obligatoire.';
    if (form.dateDebut && form.dateFin && form.dateFin < form.dateDebut)
      errs.dateFin = 'La date de fin doit être postérieure à la date de début.';
    if (!form.annee || form.annee < 2020 || form.annee > 2035)
      errs.annee = "L'année doit être comprise entre 2020 et 2035.";
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setApiError('');

    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setIsSaving(true);
    try {
      const payload: ProcedureRequest = {
        ...form,
        structureBeneficiaireId: form.structureBeneficiaireId || undefined,
        responsableId: form.responsableId || undefined,
      };
      if (isEditing && id) {
        await apiService.updateProcedure(id, payload);
      } else {
        await apiService.createProcedure(payload);
      }
      navigate('/procedures');
    } catch (err: any) {
      setApiError(err.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = <K extends keyof ProcedureRequest>(key: K, value: ProcedureRequest[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (submitted) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  if (isLoading) return <FormSkeleton />;

  return (
    <div className="w-full space-y-5">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-gray-500">
        <Link to="/procedures" className="hover:text-primary-600 font-medium transition-colors">
          Procédures
        </Link>
        <ChevronRightIcon className="h-4 w-4 text-gray-300" />
        <span className="text-gray-800 font-medium">
          {isEditing ? 'Modifier la procédure' : 'Nouvelle procédure'}
        </span>
      </nav>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary-600 to-primary-700 px-8 py-7">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <DocumentTextIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white leading-tight">
                {isEditing ? 'Modifier la procédure' : 'Nouvelle procédure'}
              </h1>
              <p className="text-primary-200 text-sm mt-0.5">
                {isEditing
                  ? 'Modifiez les informations de cette procédure.'
                  : 'Remplissez les informations pour créer une nouvelle procédure.'}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="px-8 py-7">
            {apiError && (
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3.5 rounded-xl text-sm mb-8">
                <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>{apiError}</span>
              </div>
            )}

            {/* Grille principale 2 colonnes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-8">

              {/* ── Colonne gauche ── */}
              <div className="space-y-8">

                {/* Section 1 : Informations générales */}
                <FormSection icon={DocumentTextIcon} title="Informations générales">
                  <Field label="Nom de la procédure" required error={errors.nom}>
                    <input
                      type="text"
                      value={form.nom}
                      onChange={(e) => handleChange('nom', e.target.value)}
                      className={inputCls(errors.nom)}
                      placeholder="Ex : Dématérialisation des actes de naissance"
                    />
                  </Field>

                  <Field label="Description / Code activité" error={errors.description}
                    hint="Code activité ou description synthétique (ex : 1.2.3)">
                    <textarea
                      rows={4}
                      value={form.description ?? ''}
                      onChange={(e) => handleChange('description', e.target.value)}
                      className={`${inputCls(errors.description)} resize-y`}
                      placeholder="Code activité : 1.2.3"
                    />
                  </Field>

                  <Field label="Année" required error={errors.annee} hint="Année de développement">
                    <input
                      type="number"
                      min={2020}
                      max={2035}
                      value={form.annee}
                      onChange={(e) => handleChange('annee', Number(e.target.value))}
                      className={inputCls(errors.annee)}
                    />
                  </Field>
                </FormSection>

                <hr className="border-gray-100" />

                {/* Section 2 : Calendrier */}
                <FormSection icon={CalendarDaysIcon} title="Calendrier">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Date de début" required error={errors.dateDebut}>
                      <input
                        type="date"
                        value={form.dateDebut}
                        onChange={(e) => handleChange('dateDebut', e.target.value)}
                        className={inputCls(errors.dateDebut)}
                      />
                    </Field>
                    <Field label="Date de fin" required error={errors.dateFin}>
                      <input
                        type="date"
                        value={form.dateFin}
                        min={form.dateDebut || undefined}
                        onChange={(e) => handleChange('dateFin', e.target.value)}
                        className={inputCls(errors.dateFin)}
                      />
                    </Field>
                  </div>
                </FormSection>
              </div>

              {/* ── Colonne droite ── */}
              <div className="space-y-8">

                {/* Section 3 : Organisation */}
                <FormSection icon={BuildingOffice2Icon} title="Organisation">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Structure de développement" required error={errors.structureId}>
                      {isSuperAdmin() ? (
                        <select
                          value={form.structureId}
                          onChange={(e) => handleStructureChange(e.target.value)}
                          className={selectCls(errors.structureId)}
                        >
                          <option value="">Sélectionner une structure</option>
                          {structures.map((s) => (
                            <option key={s.id} value={s.id}>{s.sigle || s.nom}</option>
                          ))}
                        </select>
                      ) : (
                        <div className="relative">
                          <input
                            type="text"
                            value={user?.structureNom || ''}
                            disabled
                            className={selectCls(undefined, true)}
                          />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <span className="text-xs text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full font-medium">
                              Votre structure
                            </span>
                          </div>
                        </div>
                      )}
                    </Field>

                    <Field label="Structure bénéficiaire" error={errors.structureBeneficiaireId}
                      hint="Optionnel">
                      <select
                        value={form.structureBeneficiaireId ?? ''}
                        onChange={(e) => handleChange('structureBeneficiaireId', e.target.value)}
                        className={selectCls(errors.structureBeneficiaireId)}
                      >
                        <option value="">Aucune / Non définie</option>
                        {structures.map((s) => (
                          <option key={s.id} value={s.id}>{s.sigle || s.nom}</option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <Field label="Responsable du suivi" error={errors.responsableId}>
                    <div className="relative">
                      <UserCircleIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      <select
                        value={form.responsableId ?? ''}
                        onChange={(e) => handleChange('responsableId', e.target.value)}
                        disabled={!form.structureId}
                        className={`pl-9 ${selectCls(errors.responsableId, !form.structureId)}`}
                      >
                        <option value="">
                          {form.structureId ? 'Sélectionner un responsable' : "Choisir une structure d'abord"}
                        </option>
                        {responsables.map((resp) => (
                          <option key={resp.id} value={resp.id}>
                            {resp.prenom} {resp.nom}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Field>
                </FormSection>

                <hr className="border-gray-100" />

                {/* Section 4 : Observations */}
                <FormSection icon={ChatBubbleBottomCenterTextIcon} title="Observations">
                  <Field
                    label="Observations"
                    hint="Notes, contexte, contraintes, informations utiles"
                  >
                    <RichTextEditor
                      value={form.observations ?? ''}
                      onChange={(html) => handleChange('observations', html)}
                      placeholder="Rédigez vos observations..."
                      minHeight={220}
                    />
                  </Field>
                </FormSection>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-8 py-5 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate('/procedures')}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Annuler
            </button>

            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 shadow-sm hover:shadow-md transition-all disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  Enregistrement...
                </>
              ) : isEditing ? (
                'Enregistrer les modifications'
              ) : (
                'Créer la procédure'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProcedureForm;
